// Gift delivery mode. Mirrors data-component="gift-delivery" from the
// approved HTML. "Add to next shipment" selects a specific upcoming
// subscription order; "Ship now" creates a separate gift shipment and
// requires an address-confirmation checkbox.
//
// TODO(runtime): hydrate upcoming shipments from
// dist/account-workflows.subscriptions (confirmed) once the HTTP read
// endpoint lands. Address is read-only here; the live flow routes through
// SHIPSTATION_UPDATE_ADDRESS (proposed - TODO runtime).
import type { ReactNode } from 'react';
import { UPCOMING_SHIPMENTS, type UpcomingShipment } from '../mockData.js';

export type DeliveryMode = 'next' | 'now';

export interface GiftDeliveryProps {
  mode: DeliveryMode;
  selectedShipmentId: string;
  addressConfirmed: boolean;
  onModeChange: (mode: DeliveryMode) => void;
  onShipmentChange: (shipment: UpcomingShipment) => void;
  onAddressConfirmChange: (confirmed: boolean) => void;
}

export function GiftDelivery({
  mode,
  selectedShipmentId,
  addressConfirmed,
  onModeChange,
  onShipmentChange,
  onAddressConfirmChange,
}: GiftDeliveryProps): ReactNode {
  return (
    <section className="card" data-component="gift-delivery">
      <div className="section-heading">
        <h3>How should this gift ship?</h3>
        <span>One-time gift</span>
      </div>
      <div className="delivery-modes" role="group" aria-label="Gift delivery">
        <button
          type="button"
          aria-pressed={mode === 'next'}
          onClick={() => onModeChange('next')}
        >
          <strong>Add to next shipment</strong>
          <small>Include with a selected subscription order</small>
        </button>
        <button
          type="button"
          aria-pressed={mode === 'now'}
          onClick={() => onModeChange('now')}
        >
          <strong>Ship now</strong>
          <small>Create a separate gift shipment</small>
        </button>
      </div>
      {mode === 'next' ? (
        <div className="shipment-choices">
          {UPCOMING_SHIPMENTS.map((shipment) => (
            <button
              key={shipment.id}
              type="button"
              className={
                'shipment-choice' +
                (shipment.id === selectedShipmentId ? ' selected-shipment' : '')
              }
              aria-pressed={shipment.id === selectedShipmentId}
              onClick={() => onShipmentChange(shipment)}
            >
              <span className="ship-radio" />
              <div>
                <strong>{shipment.label}</strong>
                <p>{shipment.products}</p>
              </div>
              <span className="ship-date">
                {shipment.monthLabel}
                <small>{shipment.dayLabel}</small>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div>
          <div className="ship-now-address">
            <span className="gift-kind">DELIVERY ADDRESS · EXAMPLE</span>
            <strong>Sarah Mitchell · Barn address</strong>
            <p>Nashville, TN · Saved address preview</p>
            <small>The complete saved address is verified before fulfillment.</small>
            <label>
              <input
                type="checkbox"
                checked={addressConfirmed}
                onChange={(e) => onAddressConfirmChange(e.target.checked)}
              />{' '}
              Confirm the complete delivery address with Sarah
            </label>
          </div>
          <div className="ship-now-details">
            <div>
              <span>Shipping cost</span>
              <strong>Pending quote</strong>
            </div>
            <div>
              <span>Dispatch timing</span>
              <strong>Confirmed at fulfillment</strong>
            </div>
          </div>
        </div>
      )}
      <div className="plan-note">
        <svg>
          <use href="#check" />
        </svg>
        <span>
          {mode === 'now'
            ? 'A separate, one-time gift shipment. Existing subscription dates and products stay unchanged.'
            : 'One-time gift on the selected shipment. It does not become a recurring subscription item.'}
        </span>
      </div>
    </section>
  );
}
